<div class="system-status-page">
  <div class="page-content">
    <div class="content-wrapper">
      <div class="main-content">
        <!-- Page Header -->
        <div class="top-header">
          <button pButton type="button" icon="pi pi-arrow-left" class="p-button-text p-button-plain back-button"
                  onclick="window.location.href='/admin'">
          </button>
          
          <div class="title-container">
            <h1 class="page-title">
              {{ 'System Status' | translate }}
            </h1>
            <button 
              type="button"
              class="version-info-trigger"
              (click)="openVersionModal()"
              [class.active]="isVersionModalOpen()"
              pTooltip="Version Information"
              tooltipPosition="bottom">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                <path d="M12 8V12M12 16H12.01" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                <circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="1.5"/>
              </svg>
            </button>
          </div>
          
          <button pButton type="button" icon="pi pi-refresh" class="p-button-text refresh-button"
                  (click)="refreshData()" [disabled]="loading()">
          </button>
        </div>

        <!-- Loading State -->
        @if (loading()) {
          <div class="loading-state">
            <div class="loading-spinner"></div>
            <span>{{ 'Loading system status...' | translate }}</span>
          </div>
        }

        <!-- Error State -->
        @else if (error()) {
          <div class="error-state">
            <div class="error-content">
              <img src="../../../assets/icons/main/light/alert-fill.svg" alt="Error" />
              <h3>{{ 'Error' | translate }}</h3>
              <p>{{ error() }}</p>
              <button pButton type="button" class="p-button" (click)="refreshData()">
                {{ 'Retry' | translate }}
              </button>
            </div>
          </div>
        }

        <!-- Module Cards -->
        @else if (moduleCards().length > 0) {
          <div class="modules-grid">
            @for (moduleCard of moduleCards(); track moduleCard.module.moduleName) {
              <div class="module-card" [ngClass]="getStatusClass(moduleCard.status)">
                <!-- Card Header -->
                <div class="card-header">
                  <div class="module-info">
                    <img [src]="getStatusIcon(moduleCard.status)" alt="Status" class="status-icon" />
                    <div class="module-details">
                      <h3 class="module-name">{{ moduleCard.displayName }}</h3>
                      <p class="module-description">{{ 'Module Status' | translate }}</p>
                    </div>
                  </div>
                  <div class="status-badge">
                    <p-tag [value]="getStatusText(moduleCard.status)"
                           [severity]="moduleCard.status === 'healthy' ? 'success' :
                                       moduleCard.status === 'warning' ? 'warn' : 'danger'">
                    </p-tag>
                  </div>
                </div>

                <!-- Card Content -->
                <div class="card-content">
                  <!-- Error Summary -->
                  @if (moduleCard.totalErrors > 0) {
                    <div class="error-summary">
                      <div class="error-header">
                        <img src="../../../assets/icons/main/light/alert-fill.svg" alt="Errors" />
                        <span class="error-count">{{ moduleCard.totalErrors }} {{ 'errors' | translate }}</span>
                      </div>
                      <div class="error-list">
                        @for (error of moduleCard.moduleErrors | slice:0:3; track error) {
                          <div class="error-item">{{ error }}</div>
                        }
                        @if (moduleCard.moduleErrors.length > 3) {
                          <div class="more-errors">
                            +{{ moduleCard.moduleErrors.length - 3 }} {{ 'more errors' | translate }}
                          </div>
                        }
                      </div>
                    </div>
                  }
                </div>

                <!-- Card Footer -->
                <div class="card-footer">
                  <!-- Module Statistics -->
                  <div class="module-stats">
                    <div class="stat-item">
                      <span class="stat-label">{{ 'Total Models' | translate }}</span>
                      <span class="stat-value">{{ getModelCount(moduleCard.module) }}</span>
                    </div>
                    <div class="stat-item">
                      <span class="stat-label">{{ 'Healthy' | translate }}</span>
                      <span class="stat-value healthy-count">{{ getHealthyModelCount(moduleCard.module) }}</span>
                    </div>
                    <div class="stat-item">
                      <span class="stat-label">{{ 'With Errors' | translate }}</span>
                      <span class="stat-value error-count">{{ getErrorModelCount(moduleCard.module) }}</span>
                    </div>
                  </div>

                  <!-- Status Bar -->
                  <div class="progress-section">
                    <div class="progress-info">
                      <span>{{ 'Health Score' | translate }}</span>
                      <span>{{ getHealthPercentage(moduleCard.module) }}% ({{ getHealthyModelCount(moduleCard.module) }}/{{ getModelCount(moduleCard.module) }})</span>
                    </div>
                    <div class="custom-status-bar">
                      <div class="status-bar-container">
                        <div class="status-segment healthy-segment"
                             [style.width.%]="getHealthPercentage(moduleCard.module)">
                        </div>
                        <div class="status-segment error-segment"
                             [style.width.%]="getErrorPercentage(moduleCard.module)">
                        </div>
                      </div>
                    </div>
                  </div>

                  <button pButton type="button" class="p-button-outlined details-button"
                          (click)="showModuleDetails(moduleCard)">
                    {{ 'Details' | translate }}
                  </button>
                </div>
              </div>
            }
          </div>
        }

        <!-- Empty State -->
        @else {
          <div class="empty-state">
            <div class="empty-content">
              <img src="../../../assets/icons/main/light/alert-line.svg" alt="No modules" />
              <h3>{{ 'No modules found' | translate }}</h3>
              <p>{{ 'No system modules are currently available' | translate }}</p>
            </div>
          </div>
        }
      </div>
    </div>
  </div>

  <!-- Details Modal -->
  <p-dialog
    [visible]="showDetailsModal"
    [modal]="true"
    [closable]="false"
    [draggable]="false"
    [resizable]="false"
    [dismissableMask]="true"
    [style]="{ width: '80vw', maxWidth: '900px' }"
    (onHide)="closeDetailsModal()"
    [header]="selectedModule() ? selectedModule()!.displayName : ''">

    <ng-template pTemplate="header">
      <div class="modal-header">
        <h2>{{ selectedModule() ? selectedModule()!.displayName : '' }}</h2>
        <button pButton type="button" icon="pi pi-times" class="p-button-text p-button-plain modal-close-btn"
                (click)="closeDetailsModal()">
        </button>
      </div>
    </ng-template>

    @if (selectedModule()) {
      <div class="modal-content">
        <!-- Module Overview -->
        <div class="module-overview">
          <h3>{{ 'Module Overview' | translate }}</h3>
          <div class="overview-stats">
            <div class="overview-item">
              <span class="overview-label">{{ 'Total Models' | translate }}</span>
              <span class="overview-value">{{ getModelCount(selectedModule()!.module) }}</span>
            </div>
            <div class="overview-item">
              <span class="overview-label">{{ 'Healthy Models' | translate }}</span>
              <span class="overview-value healthy">{{ getHealthyModelCount(selectedModule()!.module) }}</span>
            </div>
            <div class="overview-item">
              <span class="overview-label">{{ 'Models with Errors' | translate }}</span>
              <span class="overview-value error">{{ getErrorModelCount(selectedModule()!.module) }}</span>
            </div>
            <div class="overview-item">
              <span class="overview-label">{{ 'Health Score' | translate }}</span>
              <span class="overview-value">{{ getHealthPercentage(selectedModule()!.module) }}%</span>
            </div>
          </div>
          <div class="overview-progress">
            <div class="custom-status-bar">
              <div class="status-bar-container">
                <div class="status-segment healthy-segment"
                     [style.width.%]="getHealthPercentage(selectedModule()!.module)">
                </div>
                <div class="status-segment error-segment"
                     [style.width.%]="getErrorPercentage(selectedModule()!.module)">
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Module Errors -->
        @if (selectedModule()!.moduleErrors.length > 0) {
          <div class="error-section">
            <h3>{{ 'Module Errors' | translate }}</h3>
            <div class="error-list">
              @for (error of selectedModule()!.moduleErrors; track error) {
                <div class="error-item">
                  <img src="../../../assets/icons/main/light/alert-fill.svg" alt="Error" />
                  <span>{{ error }}</span>
                </div>
              }
            </div>
          </div>
        }

        <!-- Model Errors -->
        @if (selectedModule()!.modelErrors.length > 0) {
          <div class="model-section">
            <h3>{{ 'Model Errors' | translate }}</h3>
            <p-table [value]="selectedModule()!.modelErrors" [tableStyle]="{'min-width': '50rem'}">
              <ng-template pTemplate="header">
                <tr>
                  <th>{{ 'Model Name' | translate }}</th>
                  <th>{{ 'Database' | translate }}</th>
                  <th>{{ 'Logic' | translate }}</th>
                  <th>{{ 'Web API' | translate }}</th>
                  <th>{{ 'Errors' | translate }}</th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-modelError>
                <tr>
                  <td class="model-name-cell">
                    <span [pTooltip]="modelError.displayName"
                          tooltipPosition="top"
                          class="model-name-text">
                      {{ truncateText(modelError.displayName, 25) }}
                    </span>
                  </td>
                  <td>
                    <p-tag [value]="modelError.model.dbLayerCheck ? 'OK' : 'FAIL'"
                           [severity]="modelError.model.dbLayerCheck ? 'success' : 'danger'">
                    </p-tag>
                  </td>
                  <td>
                    <p-tag [value]="modelError.model.logicLayerCheck ? 'OK' : 'FAIL'"
                           [severity]="modelError.model.logicLayerCheck ? 'success' : 'danger'">
                    </p-tag>
                  </td>
                  <td>
                    <p-tag [value]="modelError.model.webApi ? 'OK' : 'FAIL'"
                           [severity]="modelError.model.webApi ? 'success' : 'danger'">
                    </p-tag>
                  </td>
                  <td>
                    @if (modelError.errors.length > 0) {
                      <div class="model-errors">
                        @for (error of modelError.errors; track error) {
                          <div class="model-error">{{ error }}</div>
                        }
                      </div>
                    } @else {
                      <span class="no-errors">{{ 'No errors' | translate }}</span>
                    }
                  </td>
                </tr>
              </ng-template>
            </p-table>
          </div>
        }

        <!-- No Errors Message -->
        @if (selectedModule()!.totalErrors === 0) {
          <div class="no-errors-section">
            <div class="no-errors-content">
              <img src="../../../assets/icons/main/light/check-circle.svg" alt="No errors" />
              <h3>{{ 'No errors found' | translate }}</h3>
              <p>{{ 'This module is operating normally' | translate }}</p>
            </div>
          </div>
        }
      </div>
    }
  </p-dialog>

  <!-- Version Info Modal -->
  <app-version-info #versionInfoModal></app-version-info>
</div>